import { useMemo, useState } from "react";
import { useCreateTermin, useProjekte } from "../../hooks/usePlanung";
import { useDeleteWorkEntry, useUpdateWorkEntry, useUpdateWorkEntryStatus } from "../../hooks/useTeam";
import type { TeamFlatRate, TeamMemberProfile, TeamWorkEntry } from "../../lib/team";
import { euro, monthLabel, shortDate, timeLabel } from "../../lib/team";
import { Dialog, DialogActions, Field } from "./TeamDialogs";

export function MemberWorklog({ member, year, entries, flatRates }: {
  member: TeamMemberProfile;
  year: number;
  entries: TeamWorkEntry[];
  flatRates: TeamFlatRate[];
}) {
  const status = useUpdateWorkEntryStatus(member.id);
  const [showCreate, setShowCreate] = useState(false);
  const [rejecting, setRejecting] = useState<TeamWorkEntry | null>(null);
  const [editing, setEditing] = useState<TeamWorkEntry | null>(null);
  const months = useMemo(() => groupMonths(entries, flatRates), [entries, flatRates]);
  const totalHours = entries.reduce((sum, entry) => sum + entry.hours, 0);
  // Lohnwert: pauschal vergütete Einsätze stecken im Festpreis und dürfen NICHT
  // zusätzlich als Stunden × Lohn zählen — sonst steht hier mehr, als die Firma
  // schuldet, und die Zahl widerspricht `payroll.verdient_gesamt` aus derselben
  // Antwort (Befund 12.08.2026). Und eine noch nicht freigegebene Pauschale ist
  // vereinbart, aber nicht verdient.
  const totalWages = entries.reduce((sum, entry) => sum + eintragLohn(entry), 0)
    + flatRates.reduce((sum, rate) => sum + (rate.freigegeben_am ? rate.betrag : 0), 0);

  return (
    <section className="team-card team-worklog">
      <header className="team-card-head worklog-head">
        <div><span className="team-section-label">Arbeitskonto</span><h2>Stunden & Pauschalen</h2></div>
        <button type="button" className="team-button primary compact" onClick={() => setShowCreate(true)}>＋ Stunden</button>
      </header>
      <div className="team-work-summary"><span><strong>{totalHours.toLocaleString("de-DE", { maximumFractionDigits: 2 })} Std.</strong> erfasst</span><span><strong>{euro(totalWages)}</strong> Lohnwert</span><span><strong>{entries.length + flatRates.length}</strong> Einträge</span></div>
      <div className="team-months">
        {months.map((month) => (
          <section className="team-month" key={month.key}>
            <header><div><h3>{monthLabel(month.key)}</h3><span>{month.entries.reduce((sum, entry) => sum + entry.hours, 0).toLocaleString("de-DE", { maximumFractionDigits: 2 })} Std.</span></div><strong>{euro(month.total)}</strong></header>
            {month.entries.map((entry) => (
              <article className="team-work-row" key={entry.id}>
                <time dateTime={entry.work_date}><strong>{new Date(`${entry.work_date}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit" })}</strong><span>{new Date(`${entry.work_date}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short" })}</span></time>
                <div className="team-work-main"><strong>{entry.projekt_title || "Ohne Projektbezeichnung"}</strong><span>{timeLabel(entry.start_at)}–{timeLabel(entry.end_at)} · {entry.hours.toLocaleString("de-DE", { maximumFractionDigits: 2 })} Std. · {euro(entry.stundenlohn)}/Std.</span>{entry.note && <small>{entry.note}</small>}{entry.ablehnung_grund && <small className="team-reason">Grund: {entry.ablehnung_grund}</small>}</div>
                <div className="team-work-status"><strong>{entry.verguetungsart === "pauschale" ? (entry.zusatzkosten ? euro(entry.zusatzkosten) : "über Pauschale") : euro(eintragLohn(entry))}</strong><StatusPill status={entry.confirm_status} /><div>{["pending_helfer", "pending_inhaber", "geplant"].includes(entry.confirm_status) ? <><button type="button" disabled={status.isPending} onClick={() => status.mutate({ slotId: entry.id, action: "confirm" })}>Bestätigen</button><button type="button" disabled={status.isPending} onClick={() => setRejecting(entry)}>Ablehnen</button></> : <button type="button" disabled={status.isPending} onClick={() => status.mutate({ slotId: entry.id, action: "reopen" })}>Neu prüfen</button>}<button type="button" onClick={() => setEditing(entry)}>Ändern</button></div></div>
              </article>
            ))}
            {month.flatRates.map((rate) => <article className="team-work-row flat" key={rate.id}><time dateTime={rate.datum}><strong>{shortDate(rate.datum).slice(0, 2)}</strong><span>Pauschale</span></time><div className="team-work-main"><strong>{rate.projekt_title || "Projektpauschale"}</strong><span>{rate.bezeichnung}</span></div><div className="team-work-status"><strong>{euro(rate.betrag)}</strong><span className={`team-pill ${rate.freigegeben_am ? "info" : "warning"}`}>{rate.freigegeben_am ? "Pauschale" : "noch nicht freigegeben"}</span></div></article>)}
          </section>
        ))}
        {!months.length && <div className="team-state"><p>Für {year} sind noch keine Stunden oder Pauschalen erfasst.</p></div>}
      </div>
      {status.isError && <p className="team-error">Der Stundenstatus konnte nicht geändert werden.</p>}
      {showCreate && <WorkEntryDialog member={member} onClose={() => setShowCreate(false)} />}
      {rejecting && <RejectDialog memberId={member.id} entry={rejecting} onClose={() => setRejecting(null)} />}
      {editing && <EntryEditDialog entry={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** Was ein Einsatz an Lohn kostet — pauschal vergütete Zeit nur mit Auslagen. */
function eintragLohn(entry: TeamWorkEntry): number {
  if (entry.verguetungsart === "pauschale") return entry.zusatzkosten;
  return entry.hours * entry.stundenlohn + entry.zusatzkosten;
}

function groupMonths(entries: TeamWorkEntry[], flatRates: TeamFlatRate[]) {
  const keys = new Set([...entries.map((entry) => entry.work_date.slice(0, 7)), ...flatRates.map((rate) => rate.datum.slice(0, 7))]);
  return [...keys].sort().reverse().map((key) => {
    const monthEntries = entries.filter((entry) => entry.work_date.startsWith(key));
    const monthRates = flatRates.filter((rate) => rate.datum.startsWith(key));
    return { key, entries: monthEntries, flatRates: monthRates, total: monthEntries.reduce((sum, entry) => sum + eintragLohn(entry), 0) + monthRates.reduce((sum, rate) => sum + (rate.freigegeben_am ? rate.betrag : 0), 0) };
  });
}

function StatusPill({ status }: { status: string }) {
  const config = status === "bestaetigt" ? ["Bestätigt", "success"] : status === "abgelehnt" ? ["Abgelehnt", "danger"] : ["Offen", "warning"];
  return <span className={`team-pill ${config[1]}`}>{config[0]}</span>;
}

function WorkEntryDialog({ member, onClose }: { member: TeamMemberProfile; onClose: () => void }) {
  const create = useCreateTermin();
  const { data: projects = [] } = useProjekte();
  const [projectId, setProjectId] = useState("");
  const today = localDateValue(new Date());
  const [date, setDate] = useState(today);
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("16:30");
  const [pause, setPause] = useState("0,5");
  const [extra, setExtra] = useState("0");
  const [note, setNote] = useState("");
  const save = () => {
    const project = projects.find((item) => item.id === projectId);
    const startAt = `${date}T${start}:00`;
    const endAt = `${date}T${end}:00`;
    create.mutate({ projekt_id: projectId, title: project?.title || "Einsatz", start_at: startAt, end_at: endAt, slots: [{ person_id: member.id, person_typ: "helfer", start_at: startAt, end_at: endAt, pause_stunden: Number(pause.replace(",", ".")) || 0, stundenlohn: member.hourly_rate, zusatzkosten: Number(extra.replace(",", ".")) || 0, note: note.trim(), confirm_status: "bestaetigt", einladungs_status: "zugesagt" }] }, { onSuccess: onClose });
  };
  const endAt = `${date}T${end}:00`;
  const einsatzBeendet = Boolean(date && end && new Date(endAt).getTime() <= Date.now());
  return (
    <Dialog title={`Stunden für ${member.name}`} onClose={onClose}>
      <div className="team-form-grid">
        <Field label="Projekt" full><select autoFocus value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">Projekt wählen</option>{projects.map((project) => <option value={project.id} key={project.id}>{project.title}{project.kunde_name ? ` · ${project.kunde_name}` : ""}</option>)}</select></Field>
        <Field label="Datum"><input type="date" max={today} value={date} onChange={(event) => setDate(event.target.value)} /></Field>
        <span />
        <Field label="Beginn"><input type="time" value={start} onChange={(event) => setStart(event.target.value)} /></Field>
        <Field label="Ende"><input type="time" value={end} onChange={(event) => setEnd(event.target.value)} /></Field>
        <Field label="Pause (Std.)"><input inputMode="decimal" value={pause} onChange={(event) => setPause(event.target.value)} /></Field>
        <Field label="Zusatzkosten (€)"><input inputMode="decimal" value={extra} onChange={(event) => setExtra(event.target.value)} /></Field>
        <Field label="Notiz" full><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></Field>
      </div>
      {!einsatzBeendet && <p className="team-dialog-hint">Arbeitszeit kann erst nach dem Ende des Einsatzes erfasst werden.</p>}
      {create.isError && <p className="team-error">Der Einsatz konnte nicht angelegt werden. Prüfe auch mögliche Doppelbelegungen.</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!projectId || !date || !start || !end || start >= end || !einsatzBeendet || create.isPending} busy={create.isPending} />
    </Dialog>
  );
}

function localDateValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function RejectDialog({ memberId, entry, onClose }: { memberId: string; entry: TeamWorkEntry; onClose: () => void }) {
  const status = useUpdateWorkEntryStatus(memberId);
  const [reason, setReason] = useState("");
  const save = () => status.mutate({ slotId: entry.id, action: "reject", grund: reason.trim() }, { onSuccess: onClose });
  return <Dialog title="Stundeneintrag ablehnen" onClose={onClose}><p className="team-dialog-copy">{shortDate(entry.work_date)} · {entry.projekt_title}</p><div className="team-form-grid"><Field label="Begründung" full><textarea autoFocus rows={4} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Was stimmt an diesem Eintrag nicht?" /></Field></div>{status.isError && <p className="team-error">Der Status konnte nicht geändert werden.</p>}<DialogActions onClose={onClose} onSave={save} disabled={!reason.trim() || status.isPending} busy={status.isPending} /></Dialog>;
}

/**
 * Einen Stundeneintrag nachtraeglich korrigieren oder loeschen.
 *
 * Laeuft ueber PATCH/DELETE /kontor/slots/{id} — dieselben Endpunkte, die auch
 * die Projektdetailseite nutzt. Das Datum haengt am Termin und wird deshalb in
 * der Planung geaendert, nicht hier.
 */
function EntryEditDialog({ entry, onClose }: { entry: TeamWorkEntry; onClose: () => void }) {
  const update = useUpdateWorkEntry();
  const remove = useDeleteWorkEntry();
  const [von, setVon] = useState(timeLabel(entry.start_at));
  const [bis, setBis] = useState(timeLabel(entry.end_at));
  const [pause, setPause] = useState(String(entry.pause_stunden ?? 0));
  const [lohn, setLohn] = useState(String(entry.stundenlohn ?? 0));
  const [zusatz, setZusatz] = useState(String(entry.zusatzkosten ?? 0));
  const [note, setNote] = useState(entry.note || "");
  const [loeschenArmed, setLoeschenArmed] = useState(false);

  const num = (v: string) => {
    const n = Number((v || "").trim().replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };
  const mitZeit = (iso: string, hhmm: string) => {
    if (!iso || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const [h, m] = hhmm.split(":").map(Number);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  const speichern = () => {
    const patch: Record<string, unknown> = { note };
    const s = mitZeit(entry.start_at, von);
    const e = mitZeit(entry.end_at, bis);
    if (s) patch.start_at = s;
    if (e) patch.end_at = e;
    const p = num(pause);
    if (p != null) patch.pause_stunden = p;
    const l = num(lohn);
    if (l != null) patch.stundenlohn = l;
    const z = num(zusatz);
    if (z != null) patch.zusatzkosten = z;
    update.mutate({ slotId: entry.id, patch }, { onSuccess: onClose });
  };

  return (
    <Dialog title="Stundeneintrag ändern" onClose={onClose}>
      <p className="team-dialog-hint">
        {shortDate(entry.work_date)} · {entry.projekt_title || "Ohne Projektbezeichnung"}
      </p>
      <div className="team-form-grid">
        <Field label="Von"><input type="time" value={von} onChange={(e) => setVon(e.target.value)} /></Field>
        <Field label="Bis"><input type="time" value={bis} onChange={(e) => setBis(e.target.value)} /></Field>
        <Field label="Pause (Std.)"><input inputMode="decimal" value={pause} onChange={(e) => setPause(e.target.value)} /></Field>
        <Field label="Stundenlohn (€)"><input inputMode="decimal" value={lohn} onChange={(e) => setLohn(e.target.value)} /></Field>
        <Field label="Zusatzkosten (€)"><input inputMode="decimal" value={zusatz} onChange={(e) => setZusatz(e.target.value)} /></Field>
        <Field label="Notiz" full><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      {(update.isError || remove.isError) && <p className="team-error">Der Eintrag konnte nicht geändert werden.</p>}
      <footer className="team-dialog-actions">
        {!loeschenArmed ? (
          <button type="button" className="team-button danger" onClick={() => setLoeschenArmed(true)}>Löschen</button>
        ) : (
          <button
            type="button"
            className="team-button danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate(entry.id, { onSuccess: onClose })}
          >
            {remove.isPending ? "Löscht …" : "Wirklich löschen"}
          </button>
        )}
        <button type="button" className="team-button secondary" onClick={onClose}>Abbrechen</button>
        <button type="button" className="team-button primary" disabled={update.isPending} onClick={speichern}>
          {update.isPending ? "Speichert …" : "Speichern"}
        </button>
      </footer>
    </Dialog>
  );
}
